/**
 * Bookkeeping for "the application can be installed".
 *
 * Chromium fires `beforeinstallprompt` once the app is installable, and only
 * that event carries a prompt the page is allowed to open. It arrives on the
 * browser's schedule — after the service worker activates and the manifest has
 * been read — so a control that offers installation has to exist *before* it
 * arrives and wait for it. That is the whole reason this state lives in a
 * module store rather than in `useState` inside a button.
 *
 * The store is a module-level singleton because the offer is a property of the
 * document, not of any one screen: the top bar and the landing header both
 * render the button, and mounting the second one must not replace the deferred
 * event the first one captured. `useSyncExternalStore` hands every subscriber
 * the same snapshot, so the two never disagree about whether a prompt exists.
 *
 * Everything here is pure or a guarded browser access, so it is unit-tested
 * directly (see `install-flow.spec.ts`).
 */

/** The Chromium-only event that carries the deferred install prompt. */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** What the install button should offer right now. */
export type InstallPhase = 'manual' | 'prompt' | 'installed';

/** The environment the button reads to pick the right wording. */
export interface InstallEnvironment {
  /** `navigator.userAgent`. */
  readonly userAgent: string;
  /** `navigator.maxTouchPoints`, which separates an iPad from Safari on macOS. */
  readonly maxTouchPoints: number;
  /** `window.matchMedia('(display-mode: standalone)').matches`. */
  readonly standalone: boolean;
  /** `(navigator as …).standalone`, iOS Safari's original flag. */
  readonly iosStandalone: boolean;
}

/** Where the deferred prompt is parked once Chromium has fired the event. */
let deferredPrompt: BeforeInstallPromptEvent | null = null;

/** Whether the app runs as an installed window rather than in a browser tab. */
let installed = false;

const listeners = new Set<() => void>();

/**
 * Snapshot handed to `useSyncExternalStore`; replaced only when it changes.
 *
 * The initial value is `manual`: before `beforeinstallprompt` arrives there is
 * no way to tell a browser that will prompt from one that never will, so the
 * button offers installation from its first paint and either gets the prompt
 * when it is clicked or explains the step the browser could not take itself.
 */
let snapshot: InstallPhase = 'manual';

function publish(): void {
  const next: InstallPhase = installed ? 'installed' : deferredPrompt ? 'prompt' : 'manual';
  if (next === snapshot) return;
  snapshot = next;
  for (const listener of listeners) listener();
}

/** Subscribe to changes; the contract `useSyncExternalStore` expects. */
export function subscribeToInstallState(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The current phase. Stable between changes, so it is safe as a snapshot. */
export function getInstallPhase(): InstallPhase {
  return snapshot;
}

/**
 * Park a deferred `beforeinstallprompt` event.
 *
 * Called from the event listener, which must already have called
 * `preventDefault()` — that suppresses the browser's own mini-infobar so the
 * button is the one place the offer lives.
 */
export function setDeferredInstallPrompt(event: BeforeInstallPromptEvent | null): void {
  deferredPrompt = event;
  publish();
}

/** The parked event, if any. Consumed by the button's click handler. */
export function getDeferredInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferredPrompt;
}

/**
 * Mark the app as installed, from the `appinstalled` event or a standalone
 * launch.
 *
 * This is deliberately *not* called after our own `prompt()` resolves: that
 * promise settles as soon as the browser's sheet is dismissed, whether or not
 * the user went through with it, so treating it as proof would hide the button
 * for someone who declined.
 */
export function markInstalled(): void {
  installed = true;
  deferredPrompt = null;
  publish();
}

/**
 * Whether the app already runs as an installed app.
 *
 * There are two spellings because Safari never adopted the standard one:
 * `display-mode: standalone` is the modern query and also covers an installed
 * desktop app, while `navigator.standalone` is iOS Safari's own boolean.
 */
export function detectStandalone(environment: InstallEnvironment): boolean {
  return environment.standalone || environment.iosStandalone;
}

/**
 * Read the environment from the browser, sanity-checked.
 *
 * `matchMedia` is wrapped because a browser that does not know the query (an
 * older Safari, some embedded webviews) must not take the app down with it —
 * the fallback is simply "not installed".
 */
export function readInstallEnvironment(): InstallEnvironment {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return { userAgent: '', maxTouchPoints: 0, standalone: false, iosStandalone: false };
  }

  const iosStandalone =
    (navigator as Navigator & { standalone?: boolean }).standalone === true;

  let standalone = iosStandalone;
  try {
    standalone = window.matchMedia('(display-mode: standalone)').matches || iosStandalone;
  } catch {
    /* The query is unsupported; `iosStandalone` is the only signal left. */
  }

  return {
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    standalone,
    iosStandalone,
  };
}

/** The four environments whose installation steps differ enough to spell out. */
export type InstallPlatform = 'ios' | 'android' | 'desktop' | 'other';

/**
 * Classify the environment.
 *
 * Order matters: an iPad reports a desktop Safari user agent, so touch support
 * is checked before the desktop branch. Android is matched on `android` rather
 * than on "mobile", because Chrome on iOS is Safari underneath and installs
 * the iOS way.
 */
export function detectInstallPlatform(environment: InstallEnvironment): InstallPlatform {
  const ua = environment.userAgent;

  // iPadOS identifies itself as "Macintosh"; `maxTouchPoints` is what tells a
  // tablet apart from a laptop, and it has to be ruled out before `desktop`.
  const iPad = /Macintosh/.test(ua) && environment.maxTouchPoints > 1;

  if (/iPhone|iPad|iPod/.test(ua) || iPad) return 'ios';
  if (/Android/.test(ua)) return 'android';
  if (/Windows|Macintosh|Linux|CrOS/.test(ua)) return 'desktop';
  return 'other';
}

/** Which message key explains installation for a platform. */
export function installHintKey(platform: InstallPlatform): string {
  return platform === 'ios'
    ? 'pwa.installIosHint'
    : platform === 'android'
      ? 'pwa.installAndroidHint'
      : 'pwa.installBrowserHint';
}

/**
 * Reset the module state.
 *
 * Test-only: the store is a singleton by design, so a test that changes it has
 * to put it back. Subscribers are intentionally left alone — a mounted button
 * must survive a reset.
 */
export function resetInstallStateForTests(): void {
  deferredPrompt = null;
  installed = false;
  snapshot = 'manual';
}
